// The hub shell (R229; mockup round fifteen B, card 02).
//
// The hub is where a signed-in account sits between worlds. It opens after
// the landing screen has a session and it closes when the player presses a
// mode: it resolves with the join answer, `main.ts` dials it, and the game
// boots behind it. Going back to the menu is a page reload, exactly as it
// was before this entry.
//
// The shape, from card 02 of round fifteen:
//
//   header   the mark, the name with its one-time rename, the tier badge
//            and a sign-out control
//   strip    NEWS · SHOP · MODDING · HELP · SETTINGS
//   pane     the open section, scrolling, with a fade at its foot while
//            content is still below; the legal links, which a paid
//            service operated from Germany must carry one press from
//            every page, close the content and scroll with it (R1741)
//   dock     PLAY · RANKS · RECORD · CLAN · WARDROOM, in the thumb zone
//
// The shell owns the chrome and the cues. A panel owns its content
// (`panel.ts`), and `hubstate.ts` owns every figure either of them prints.
//
// Rendering: the shell rebuilds the pane's markup and writes it only when
// it changed, which is `overlay.ts`'s rule. A one-second tick drives the
// countdowns and, through the cache's own life, the refresh of the mode
// schedule and the account. Focus survives a rewrite: the box a player is
// typing in is found again by its `data-in` name.
//
// The keyboard and the screen reader (ui-map-interface.md §16.3, R1738):
// the open tab wears `aria-current`, the dock stands before the pane in the
// markup so the Tab key reaches the five primary sections first, one polite
// live region states the busy line, the refusals and the rename result, and
// the address carries the open section as `?tab=`, so a reload opens it.

import './../hub.css';
// The kit of the ☸ OPTIONS menu, which the SETTINGS panel shares with the
// game, and the hub cards in its look (R1731).
import '../options/options.css';
import '../choice/choice.css';
import '../options/cards.css';

import type { AudioDirector } from '../../audio/director';
import {
  authLogout, dismissNotice, getMe, getModes, join as apiJoin, messageOf, renameAccount,
  type AccountNotice, type Join, type JoinRequest, type Me, type ModeList,
} from '../../net/api';
import { clearAllTokens } from '../../net/token';
import { factionName } from '../../world';
import { badgePicture } from '../chart/faction-key';
// The breakpoint and the redraw cadence ride presentation.toml
// [presentation.chrome] (R312).
import {
  CHROME_ARM_MS, CHROME_TICK_MS, CHROME_WIDE_PX, HUB_HEADER_BADGE_PX, LOCKUP,
} from '../../gen/presentation';
import { ArmGate } from '../arm';
import { devBrand, signSVGElement } from '../sign';
import { pageTabIcon } from '../tabicon';
import { PressRoute, keepFocus } from '../pressroute';
import { FocusMarkupHost } from '../markuphost';
import { armFaces, button } from '../choice/choice';
import { hub as S, PAGE_PREFIX } from '../../gen/strings';
import { ensureThemeBodies, onLookChange, wearTheme } from '../themestate';
import {
  HubData, TTL_LIVE, TTL_ON_OPEN, hubFaction,
  renameAsk, renameBack, renameClose, renameDone, renameFailed, renameIdle,
  renameMessageFor, renameOpen, renameStart, renameTyped, seatUnread, tierOf,
  type RenameState,
} from './hubstate';
import {
  PLAN_WORDS, SECTION_PARAM, TAB_KEYS, TAB_PARAM, card, ctlRow, errLine, esc, lede, readSection, rows, tabButton, wireOnce,
  type PageKey, type Panel, type PanelContext, type SectionAt, type TabKey,
} from './panel';
import { createPlayPanel } from './panels/play';
import { createRanksPanel } from './panels/ranks';
import { createRecordPanel } from './panels/record';
import { createClanPanel } from './panels/clan';
import { createWardroomPanel } from './panels/wardroom';
import { createNewsPanel } from './panels/news';
import { createShopPanel } from './panels/shop';
import { createModsPanel } from './panels/mods';
import { createSupportPanel } from './panels/support';
import { createSettingsPanel } from './panels/settings';
import { reportSignInOnce } from '../../net/fairplay';
import { sellsNothing } from '../../platform/platform';

/** What the caller knows before the hub opens. */
export interface HubOptions {
  me: Me;
  /** The sound director, where this client has one. Absent leaves the hub
   *  silent: every call on it is optional-chained. */
  audio?: AudioDirector;
  /** A line to show at once, for example a join that was refused. */
  notice?: string;
  /**
   * The section the hub opens on, where the caller names one. A link on a
   * sign-in provider comes back to `?tab=settings`, so the person lands on
   * the row they pressed (R621). An absent value opens PLAY, which is what
   * every other way in gets. `fast/entry.ts` checks the name against
   * `TAB_KEYS` before it reaches here.
   */
  tab?: TabKey;
  /**
   * The page inside that section the hub opens on: `?tab=clan&page=cup`,
   * the deep link of a cup post, opens the CUP page (R1649).
   * `fast/entry.ts` checks the page against `TAB_PAGES` of the section.
   */
  page?: PageKey;
  /** The world whose live volume the chronicle book opens on, or 0: the
   *  way back from a replay link (R1684). */
  book?: number;
  /** A section word of the open panel that the entry read off the address
   *  before the landing screen: `lobby<code>` of `?lobby=` (§16.17). The
   *  section word of the address itself (`SECTION_PARAM`) yields to it. */
  section?: string;
}

/** One tab, in either chrome. The name is the whole button: a section
 *  button carries no glyph (§16.3, R231). */
interface Tab {
  key: TabKey;
  name: string;
}

/** The bottom dock: the five sections a player presses every visit. The
 *  keys stay here and the five names are the locale's own list, in the
 *  same order. */
const DOCK: Tab[] = TAB_KEYS.slice(0, 5).map((key, i) => ({ key, name: S.dock_names[i] }));

/** The utility strip under the header: the five occasional ones. */
const STRIP: Tab[] = TAB_KEYS.slice(5).map((key, i) => ({ key, name: S.strip_names[i] }));

/** The section a press or an address opens: a store app shows no SHOP
 *  (mobile-apps.md §8, R2927), so a way into the shop opens PLAY. */
function shownTab(tab: TabKey): TabKey {
  return tab === 'shop' && sellsNothing() ? 'play' : tab;
}

/** The ten sections, in the order `TAB_KEYS` names them. Each one reads
 *  its own key, so the shell keys the map by what the panel says. */
const PANELS: ((ctx: PanelContext) => Panel)[] = [
  createPlayPanel, createRanksPanel, createRecordPanel, createClanPanel, createWardroomPanel,
  createNewsPanel, createShopPanel, createModsPanel, createSupportPanel, createSettingsPanel,
];

/** The legal strip. The five destinations are separate pages, and their
 *  names are the locale's own list, in the same order. The history is a
 *  section of the about page, so it has no link of its own (R1736). The
 *  href takes PAGE_PREFIX, so the row leads to the documents in the
 *  language this page picked (R856). */
const FOOT_HREFS = [
  'imprint', 'privacy', 'terms', 'about', 'contact',
].map((id) => `${PAGE_PREFIX}/${id}`);

/**
 * The Aquaphobia sign, without its plate (brand.md §1, R1644). The header
 * already sits on the deep water the plate stands for. With the wordmark
 * beside it, it is the header lockup (brand.md §2, R1645). A page of the
 * dev identity gives it the dev corner, and the stylesheet hides the
 * corner where the DEV cell of the environment tag stands beside it
 * (brand.md §15.2, R2880).
 */
/**
 * One notice card of a moderation decision (deployment.md §7.6, R3125): the
 * statement of reasons, or the decision on a report, of an account that
 * holds no mail address. The server wrote the title and the paragraphs in
 * the stored language of the account; the card escapes them and adds the
 * DISMISS control.
 */
export function noticeCard(n: AccountNotice): string {
  const paras = n.body.split(/\n\s*\n/).map((p) => p.trim()).filter((p) => p !== '');
  return card(n.subject, '§', S.notice_more,
    paras.map(lede).join('')
    + ctlRow(button(S.notice_dismiss, { act: 'notice-dismiss', id: String(n.id) })),
    'notice');
}

function hubMark(): string {
  return signSVGElement(LOCKUP.headerPx, { plate: false, corner: devBrand() }, 'class="glyph" aria-hidden="true"');
}

/** Gives a box the focus back after a rewrite, with the caret at the end. */
function focusEnd(box: HTMLInputElement | HTMLTextAreaElement): void {
  box.focus();
  const n = box.value.length;
  try { box.setSelectionRange(n, n); } catch { /* a type with no range */ }
}

/** The selection of a box and its scroll, or null for a type with no range. */
function caretOf(box: HTMLInputElement | HTMLTextAreaElement): { a: number; b: number; top: number } | null {
  try {
    const a = box.selectionStart;
    const b = box.selectionEnd;
    return a === null || b === null ? null : { a, b, top: box.scrollTop };
  } catch {
    return null;
  }
}

/** Gives a rewritten box the focus back with the selection it had, or
 *  with the caret at the end where the selection is unknown. */
function focusAt(
  box: HTMLInputElement | HTMLTextAreaElement, caret: { a: number; b: number; top: number } | null,
): void {
  if (caret === null) {
    focusEnd(box);
    return;
  }
  box.focus();
  try { box.setSelectionRange(caret.a, caret.b); } catch { /* a type with no range */ }
  box.scrollTop = caret.top;
}

/**
 * Reads the section word the address names (`SECTION_PARAM`, R1731,
 * ui-map-interface.md §16.16) and takes the word off the address bar in
 * the same breath. A word the open panel does not know is taken off too,
 * and opens nothing.
 */
function takeSection(): string | undefined {
  const url = new URL(location.href);
  if (!url.searchParams.has(SECTION_PARAM)) return undefined;
  const section = readSection(url.searchParams);
  url.searchParams.delete(SECTION_PARAM);
  history.replaceState(history.state, '', url.toString());
  return section;
}

/**
 * Writes the open section into the address as `?tab=` (R1738), so a reload
 * opens the same section: `fast/entry.ts` reads the word and hands it back
 * to the hub. PLAY is the section a plain address opens, so it writes no
 * word, and `null` takes the word off for a hub that leaves for a world.
 * Every other word of the address stays. The write is a `replaceState`: a
 * section is no history entry, and the hub holds no `popstate` handler.
 */
function writeTab(tab: TabKey | null): void {
  const url = new URL(location.href);
  if (tab === null || tab === 'play') url.searchParams.delete(TAB_PARAM);
  else url.searchParams.set(TAB_PARAM, tab);
  if (url.href === location.href) return;
  history.replaceState(history.state, '', url.toString());
}

/**
 * Shows the hub and resolves with the join the player chose.
 *
 * It never rejects. Every failure renders inline and the hub stays up,
 * because there is nowhere else for the player to go.
 */
export function showHub(opts: HubOptions): Promise<Join> {
  return new HubScreen(opts).run();
}

class HubScreen {
  private root = document.createElement('div');
  private headEl = document.createElement('div');
  private renameEl = document.createElement('div');
  private stripEl = document.createElement('div');
  private paneEl = document.createElement('div');
  private innerEl = document.createElement('div');
  private dockEl = document.createElement('div');
  private footEl = document.createElement('div');
  /** The one polite live region of the hub (R1738). `announce` writes it. */
  private liveEl = document.createElement('div');
  /** The lines the last render stated, so a line is said once when it
   *  comes and never again on a redraw that keeps it. */
  private heard = new Set<string>();

  private me: Me;
  private audio?: AudioDirector;
  private notice: string;
  private error = '';
  private busy = '';

  private tab: TabKey = 'play';
  private panels = new Map<TabKey, Panel>();
  private rename: RenameState = renameIdle();
  /** The dots that were on at the last render, so a dot that appears can
   *  play its cue exactly once. */
  private dotsOn = new Set<TabKey>();

  private data: HubData;
  /** The server's instant less the browser's, in seconds. Every countdown
   *  is measured on the server's clock (R220). */
  private clockSkew = 0;

  private timer = 0;
  private frame = 0;
  private done!: (j: Join) => void;
  private closed = false;

  /** The two-press gate of the header's SIGN OUT control (`ui/arm.ts`,
   *  R696). Signing out ends the session on this device, and a guest seat
   *  goes for good, so the control takes a second press. */
  private gate = new ArmGate(CHROME_ARM_MS, () => this.render(true));

  /** The press router of the root (R1206). Set by `run`. */
  private press: PressRoute | null = null;

  // A rewrite of the head and the tab rows gives the keyboard focus back
  // to the control that held it, so the armed SIGN OUT takes its second
  // key press (R1698).
  private readonly headHost = new FocusMarkupHost(this.headEl);
  private readonly stripHost = new FocusMarkupHost(this.stripEl);
  private readonly dockHost = new FocusMarkupHost(this.dockEl);
  private lastRename = '';
  private lastPane = '';

  constructor(opts: HubOptions) {
    this.me = opts.me;
    // The one `signin` signal of this tab (deployment.md §7.7, R1310): a
    // sign-in with Apple or Google comes back by a redirect and never
    // passes the call that sends it.
    reportSignInOnce(opts.me.account.id);
    this.audio = opts.audio;
    this.notice = opts.notice ?? '';
    if (opts.tab !== undefined) this.tab = shownTab(opts.tab);
    this.root.id = 'hub';
    this.data = new HubData(() => this.refresh());

    this.headEl.className = 'top';
    this.renameEl.className = 'rename';
    this.stripEl.className = 'strip';
    this.paneEl.className = 'pane';
    this.innerEl.className = 'paneinner';
    this.dockEl.className = 'dock';
    // The landmarks of the hub (§16.3, R2153): the strip and the dock are
    // two navigation regions with two names, and the pane is the one main
    // region. The rail of a section shell is a `nav` inside the main region.
    this.stripEl.setAttribute('role', 'navigation');
    this.stripEl.setAttribute('aria-label', S.nav_strip);
    this.dockEl.setAttribute('role', 'navigation');
    this.dockEl.setAttribute('aria-label', S.nav_dock);
    this.paneEl.setAttribute('role', 'main');
    this.footEl.className = 'foot';
    this.liveEl.className = 'live';
    this.liveEl.setAttribute('role', 'status');
    this.liveEl.setAttribute('aria-live', 'polite');
    const fade = document.createElement('div');
    fade.className = 'fade';
    this.paneEl.append(this.innerEl, fade);
    // The dock stands after the strip in the markup, so the Tab key reaches
    // the five primary sections before the controls of the pane (R1738).
    // On the screen the dock is the last thing, because it is the one a
    // thumb reaches: `hub.css` gives the pane and the dock an `order` in
    // the column of `#hub`. The legal row is the last child of the pane's
    // scroller, so it scrolls with the content (R1741); `renderPane` puts
    // it back after every rewrite.
    this.root.append(
      this.headEl, this.renameEl, this.stripEl, this.dockEl, this.paneEl, this.liveEl);

    this.wearBanner();
    pageTabIcon()?.setInbox(seatUnread(this.me));

    const ctx = this.context();
    for (const make of PANELS) {
      const panel = make(ctx);
      this.panels.set(panel.key, panel);
    }
    if (opts.page !== undefined) this.panels.get(this.tab)?.openPage?.(opts.page, opts.book ?? 0);
    // A section of a panel in the section shell rides its own word,
    // `?tab=settings&section=alerts` (R1731, §16.16). `fast/entry.ts` took
    // `tab` off the bar already; the hub takes `section` off it here, so a
    // reload opens the plain section, and the panel drops a word it does
    // not know.
    const fromAddress = takeSection();
    const section = opts.section ?? fromAddress;
    if (section !== undefined) this.panels.get(this.tab)?.openSection?.(section);
  }

  /**
   * Takes a fresh account and wears whatever banner it now flies (R242).
   *
   * Every write of `this.me` goes through here, so the theme follows the
   * account and the clan and never a clock. The account cell is refreshed
   * on its own life, so a player who joins a clan in another tab takes the
   * clan's colours at the next read.
   */
  private takeMe(me: Me): void {
    this.me = me;
    this.wearBanner();
    // The live tab icon marks a hidden hub with the INBOX items the seats
    // hold unread (R1653).
    pageTabIcon()?.setInbox(seatUnread(me));
  }

  /** Writes the theme the look and the banner resolve to. A repeat of the
   *  theme already on the root does no style work at all. */
  private wearBanner(): void {
    wearTheme(hubFaction(this.me));
  }

  /** True while the pane is wide enough for the two-column layout. */
  private wide(): boolean {
    return this.root.clientWidth >= CHROME_WIDE_PX;
  }

  /** Drops the look-change listener. Set by `run`. */
  private offLook: (() => void) | null = null;

  private context(): PanelContext {
    return {
      me: () => this.me,
      // The cell still holds the account from before the write, and the
      // next `readClock` would take it back, so the write drops it.
      setMe: (me) => { this.takeMe(me); this.data.drop('me'); this.refresh(); },
      data: this.data,
      now: () => Math.floor(Date.now() / 1000) + this.clockSkew,
      refresh: () => this.refresh(),
      join: (req) => this.doJoin(req),
      go: (tab, page) => this.select(tab, page),
      wide: () => this.wide(),
      play: (id) => this.audio?.play(id),
      musicSample: (on) => {
        const a = this.audio;
        if (!a || this.closed) return;
        a.menuMusic(!on);
        if (on) a.musicPlay();
      },
    };
  }

  run(): Promise<Join> {
    document.body.appendChild(this.root);
    writeTab(this.tab);
    // The hub is a menu, so it holds the menu playlist while it is up
    // (audio.md §3.6, R227). The landing has usually turned it on already;
    // the call is idempotent.
    this.audio?.menuMusic(true);
    // A theme pick, a theme code or a refreshed custom body repaints the
    // chrome at once (R814). The bodies refresh once per page load.
    this.offLook = onLookChange((look) => { wearTheme(hubFaction(this.me), look); this.refresh(); });
    void ensureThemeBodies();
    this.panels.get(this.tab)?.open?.();
    this.render(true);
    // One second, because that is the smallest step a countdown takes. The
    // write is skipped where the markup did not change, so a still section
    // costs one string compare a second.
    this.timer = window.setInterval(() => this.render(false), CHROME_TICK_MS);
    window.addEventListener('resize', this.onResize);
    this.innerEl.addEventListener('scroll', this.onScroll, { passive: true });
    // The header, the rename tray, the strip and the dock are fixed chrome
    // and act on the press. The pane scrolls under the finger, so a control
    // inside it acts on the release (ui-map-interface.md §16.8, R1206).
    this.press = new PressRoute(this.root, {
      // A disabled kit button keeps its act but takes no press: a
      // browser raises the pointer events of a disabled button too.
      control: '[data-act]:not(:disabled)',
      scrolls: (target) => this.innerEl.contains(target),
      act: (el) => this.onAct(el),
    });
    return new Promise<Join>((resolve) => { this.done = resolve; });
  }

  /** Takes the hub down. Every panel is destroyed first, so the CLAN
   *  panel's chat socket is closed before `main.ts` opens the in-game one. */
  private close(j: Join) {
    if (this.closed) return;
    this.closed = true;
    this.gate.clear(false);
    this.press?.destroy();
    this.press = null;
    window.clearInterval(this.timer);
    if (this.frame) cancelAnimationFrame(this.frame);
    window.removeEventListener('resize', this.onResize);
    this.offLook?.();
    this.offLook = null;
    for (const p of this.panels.values()) {
      p.close?.();
      p.destroy?.();
    }
    this.root.remove();
    // The address of a world names no section of the hub.
    writeTab(null);
    // The music goes back to the player's own playlist for the world.
    this.audio?.menuMusic(false);
    this.done(j);
  }

  // --- actions ---

  /** Runs one call with the hub in its busy state. */
  private async guard(label: string, fn: () => Promise<void>) {
    if (this.busy !== '') return;
    this.busy = label;
    this.error = '';
    this.notice = '';
    this.render(true);
    try {
      await fn();
    } catch (err) {
      this.error = messageOf(err);
      this.audio?.play('sfx-menu-error');
    } finally {
      this.busy = '';
      this.render(true);
    }
  }

  private doJoin(req: JoinRequest) {
    this.audio?.play('sfx-menu-launch');
    void this.guard(S.busy_join, async () => {
      const j = await apiJoin(req);
      this.close(j);
    });
  }

  /** Opens one section. `page` is a page of it that a press in another
   *  section named (`ctx.go`, R1738): the panel takes it before `open`. */
  private select(want: TabKey, at?: PageKey | SectionAt) {
    const tab = shownTab(want);
    if (tab !== want) at = undefined;
    if (tab === this.tab) {
      if (at === undefined) return;
      this.point(tab, at);
      this.render(true);
      return;
    }
    this.audio?.play('sfx-menu-tab');
    this.panels.get(this.tab)?.close?.();
    this.tab = tab;
    writeTab(tab);
    if (at !== undefined) this.point(tab, at);
    this.panels.get(tab)?.open?.();
    this.innerEl.scrollTop = 0;
    // A new section is a new pane: force the write rather than diff against
    // the section that just left.
    this.lastPane = '';
    this.render(true);
  }

  /** Opens the page or the section `at` names on the panel `tab`, before
   *  its `open` (§16.16). */
  private point(tab: TabKey, at: PageKey | SectionAt): void {
    const panel = this.panels.get(tab);
    if (typeof at === 'string') panel?.openPage?.(at);
    else panel?.openSection?.(at.section);
  }

  private signOut() {
    this.audio?.play('sfx-menu-press');
    void this.guard(S.busy_signout, async () => {
      await authLogout();
      // The session cookie is gone, so every cell in the cache is now the
      // answer for an account this browser no longer is — and so is every
      // resume token, which would otherwise put the next identity in this
      // account's seats. A reload is the honest way back to the landing
      // screen, and it is the same way back the rest of the client uses.
      clearAllTokens();
      location.reload();
    });
  }

  /**
   * Carries the box to the confirmation (R249).
   *
   * The rename is spent by the call and not by the answer, so the player
   * reads the picked name once more before the call goes out. A name the
   * box rules refuse stays on the box and says why.
   */
  private askRename() {
    const next = renameAsk(this.rename);
    this.audio?.play(next.confirm ? 'sfx-menu-press' : 'sfx-menu-error');
    this.rename = next;
    this.render(true);
  }

  private submitRename() {
    const want = this.rename.pending;
    if (!this.rename.confirm || want === '' || this.rename.busy) {
      this.audio?.play('sfx-menu-error');
      return;
    }
    this.rename = renameStart(this.rename);
    this.render(true);
    renameAccount(want).then(
      (out) => {
        this.rename = renameDone(this.rename);
        this.takeMe({ ...this.me, account: out.account });
        this.data.drop('me');
        this.audio?.play('sfx-menu-rename');
        this.render(true);
        this.say(S.rename_said({ name: out.account.name }));
      },
      (err) => {
        this.rename = renameFailed(this.rename, err);
        this.audio?.play('sfx-menu-error');
        this.render(true);
      },
    );
  }

  // --- rendering ---

  /** Asks for a redraw on the next frame. Several calls in one frame make
   *  one render. */
  private refresh() {
    if (this.closed || this.frame !== 0) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = 0;
      this.render(false);
    });
  }

  private render(force: boolean) {
    if (this.closed) return;
    this.readClock();
    this.renderHead(force);
    this.renderRename(force);
    this.renderTabs(force);
    this.renderPane(force);
    this.renderFoot();
    this.updateFade();
    this.announce();
  }

  /**
   * The lines the hub states now that a screen reader must hear (R1738):
   * the busy line of the header, the notice, and every refusal line. A
   * refusal is the `errLine` of `panel.ts`, so the one `.err` class routes
   * the shell's refusal and every panel's refusal here alike.
   */
  private spoken(): string[] {
    const out: string[] = [];
    if (this.busy !== '') out.push(S.busy_line({ label: this.busy }));
    if (this.notice !== '') out.push(this.notice);
    for (const el of this.root.querySelectorAll('.err')) {
      const text = (el.textContent ?? '').trim();
      if (text !== '') out.push(text);
    }
    return out;
  }

  /**
   * Says each line that is new since the last render, once. The shell
   * redraws every second, and a line that stays on the screen must not be
   * said again; a line that goes and comes back is said again.
   */
  private announce() {
    const now = this.spoken();
    const fresh = now.filter((line) => !this.heard.has(line));
    this.heard = new Set(now);
    if (fresh.length > 0) this.say(fresh.join(' '));
  }

  /** Writes one statement into the live region. */
  private say(text: string) {
    this.liveEl.textContent = text;
  }

  /**
   * Keeps the schedule and the account fresh, and takes the server's own
   * instant from the schedule answer.
   *
   * Both cells live 30 s while PLAY is open, because that is the section
   * whose figures move; every other section lets them go stale, and the
   * cache still answers with what it holds.
   */
  private readClock() {
    const ttl = this.tab === 'play' ? TTL_LIVE : TTL_ON_OPEN;
    const modes = this.data.cell<ModeList>('modes', ttl, () => getModes());
    if (modes.state === 'ready' && modes.value) {
      this.clockSkew = modes.value.now - Math.floor(Date.now() / 1000);
    }
    const me = this.data.cell<Me>('me', ttl, () => getMe());
    if (me.state === 'ready' && me.value && me.value !== this.me) this.takeMe(me.value);
  }

  /**
   * The faction badge of the header (§16.3, R1781).
   *
   * It marks the banner the account belongs to now. `hubFaction` decides
   * which one: the clan's while the account is in a clan, and the
   * account's own otherwise, so the header and the theme always say the
   * same thing. The badge is the one of the faction key, in the modern
   * reading, because the hub holds no world and so no hold reading.
   *
   * An account with no faction gets no badge, which is honest: a first
   * join rolls one, and a header that marked a banner too early would mark
   * the wrong one.
   */
  private bannerBadge(): string {
    const id = hubFaction(this.me);
    if (id <= 0) return '';
    const pic = badgePicture(id, 'm', HUB_HEADER_BADGE_PX);
    if (pic === '') return '';
    const name = esc(factionName(id));
    return `<span class="fbadge" role="img" title="${name}" aria-label="${name}">${pic}</span>`;
  }

  private renderHead(force: boolean) {
    const tier = tierOf(this.me);
    const guest = this.me.account.kind === 'guest';
    // The pencil is drawn from the account's own flag and never from a
    // refusal, and it goes for good once the change has landed (R226).
    // Its name is the act and the glyph is hidden (R2153).
    const pencil = this.me.account.canRename && !this.rename.done
      ? `<button type="button" class="icon" data-act="rename" title="${S.rename_title}"`
        + ` aria-label="${S.rename_card}"><span aria-hidden="true">✎</span></button>`
      : '';
    // The environment tag of the meta config, such as [DEV/BETA] (§16.3,
    // R2872). An environment with no tag shows none.
    // The tag is a chip of two cells, the environment and the stage
    // (DEV | BETA). A narrow screen hides the first cell (R2872).
    const [env, stage] = (this.me.hubTag ?? '').split('/', 2);
    const tag = !this.me.hubTag ? ''
      : `<span class="envtag" title="${esc(this.me.hubTag)}"><span class="env">${esc(env)}</span>`
        + (stage ? `<span class="stage">${esc(stage)}</span>` : '') + '</span>';
    // `tagged`: the DEV cell stands beside the sign, so the dev corner
    // shows only where a narrow screen hides that cell (R2880).
    const tagged = tag !== '' && env !== '';
    let html = `<span class="lockup${tagged ? ' tagged' : ''}">${hubMark()}<span class="wm">${S.wordmark}</span></span>${tag}`
      + `<span class="pname">${this.bannerBadge()}<b>${esc(this.me.account.name)}</b>${pencil}</span>`
      + '<span class="sp"></span>'
      // A guest's badge carries `guest`: a narrow screen hides it, so the
      // guest name beside its faction badge shows whole (R2208).
      // The badge names the one plan word of the hub (R2046).
      + `<span class="tierbadge ${tier}${guest ? ' guest' : ''}">${PLAN_WORDS[tier]}</span>`
      + `<button type="button" class="quiet${this.gate.is('signout') ? ' armed' : ''}" data-act="signout">`
      // The armed face names the act, and the armed END GUEST SEAT states
      // what the second press loses: the captains and the Honour (R1734,
      // R2178). Both faces share one cell, so the press keeps its width
      // (R2069), and the guest face takes two lines, so it is no wider
      // than the rest face.
      + `${armFaces(guest ? S.end_guest_seat : S.sign_out,
        (guest ? S.end_guest_seat_armed : S.sign_out_armed).split('\n').join('<br>'),
        this.gate.is('signout'))}</button>`;
    if (this.busy !== '') {
      html += `<span class="more">${S.busy_line({ label: esc(this.busy) })}</span>`;
    }
    this.headHost.write(html, force);
  }

  /**
   * The rename tray (R249).
   *
   * It is drawn in the hub's own vocabulary — a `card`, a `form`, a `lede`
   * and a row of kit buttons — because it is part of this page and not a
   * dialogue of its own (`panel.ts`, ui-map-interface.md §16.3).
   *
   * The typed value is **not** part of the markup the shell compares. The
   * shell redraws once a second. A markup string that carried the box's
   * value would differ after every keystroke, rewrite the tray, and take
   * the caret out of the box the player types in. The shell writes the
   * value on to the element after the markup lands instead.
   */
  private renderRename(force: boolean) {
    let html = '';
    if (this.notice !== '') html += `<div class="hint">${esc(this.notice)}</div>`;
    html += errLine(this.error);
    for (const n of this.me.notices ?? []) html += noticeCard(n);
    if (this.rename.open) {
      html += this.rename.confirm ? this.renameConfirmCard() : this.renameEditCard();
    }
    this.renameEl.style.display = html === '' ? 'none' : '';
    if (html === this.lastRename && !force) return;
    this.lastRename = html;
    const held = document.activeElement === this.renameBox();
    keepFocus(this.renameEl, () => { this.renameEl.innerHTML = html; });
    this.wireRenameInput();
    const box = this.renameBox();
    if (box) {
      box.value = this.rename.value;
      if (held) focusEnd(box);
    }
  }

  private renameBox(): HTMLInputElement | null {
    return this.renameEl.querySelector<HTMLInputElement>('input[data-in="rename"]');
  }

  /** The refusal line, in the card that raised it. */
  private renameError(): string {
    if (this.rename.error === '') return '';
    const extra = this.rename.message === '' ? '' : ` — ${this.rename.message}`;
    return errLine(renameMessageFor(this.rename.error) + extra);
  }

  /** Step one: the box. */
  private renameEditCard(): string {
    return card(S.rename_card, '✎', S.rename_more,
      lede(S.rename_lede)
      + '<div class="form">'
      + `<input data-in="rename" type="text" maxlength="24" placeholder="${S.rename_placeholder}" aria-label="${S.rename_placeholder}">`
      + '</div>'
      + ctlRow(
        button(S.save, { act: 'rename-save' }, { variant: 'primary' })
        + button(S.cancel, { act: 'rename-cancel' }))
      + this.renameError());
  }

  /**
   * Step two: the confirmation.
   *
   * It names the value the call sends, which is the value the server
   * stores, and it says once more that this is the one free change.
   */
  private renameConfirmCard(): string {
    return card(S.rename_confirm_card, '✎', S.rename_more,
      rows([
        [S.row_name_now, this.me.account.name],
        [S.row_name_new, this.rename.pending, 'acc'],
      ])
      + lede(S.rename_confirm_lede)
      + lede(S.rename_confirm_join)
      // A call in flight takes both controls away. A second press would
      // send a second call, and the second call is the one the server
      // answers with `rename_spent`.
      + ctlRow(this.rename.busy
        ? button(S.saving, { act: 'rename-confirm' }, { variant: 'primary', disabled: true })
        : button(S.use_this_name, { act: 'rename-confirm' }, { variant: 'primary' })
          + button(S.go_back, { act: 'rename-back' }))
      + this.renameError());
  }

  private renderTabs(force: boolean) {
    const strip = STRIP.filter((t) => shownTab(t.key) === t.key).map((t) => this.tabHtml(t)).join('');
    const dock = DOCK.map((t) => this.tabHtml(t)).join('');
    this.stripHost.write(strip, force);
    this.dockHost.write(dock, force);
  }

  private tabHtml(t: Tab): string {
    const on = this.tab === t.key;
    // A dot is the section's own answer, and it never shows on the section
    // that is open: opening it is what clears it.
    const dot = !on && (this.panels.get(t.key)?.dot?.() ?? false);
    if (dot && !this.dotsOn.has(t.key)) {
      this.dotsOn.add(t.key);
      this.audio?.play('sfx-menu-badge');
    } else if (!dot) {
      this.dotsOn.delete(t.key);
    }
    return tabButton(t.key, t.name, on, dot);
  }

  private renderPane(force: boolean) {
    this.innerEl.classList.toggle('wide', this.wide());
    const panel = this.panels.get(this.tab);
    const html = panel ? panel.html() : '';
    if (html === this.lastPane && !force) return;
    this.lastPane = html;
    // The box a player is typing in must survive a rewrite. It is found
    // again by its `data-in` name, and the caret goes to the end.
    // A text area counts too (the Captain Designer, R1240), and the caret
    // stays where it stood, so a rewrite in the middle of a backstory does
    // not throw the caret to the end.
    const active = document.activeElement as HTMLInputElement | HTMLTextAreaElement | null;
    const keep = active && this.innerEl.contains(active) ? active.dataset.in ?? '' : '';
    const caret = keep !== '' ? caretOf(active!) : null;
    // A control that held the keyboard focus takes it back after the
    // rewrite, so an armed button takes its second key press (R1698).
    keepFocus(this.innerEl, () => {
      this.innerEl.innerHTML = html;
      this.innerEl.append(this.footEl);
      panel?.wire?.(this.innerEl);
    });
    if (keep !== '') {
      const back = this.innerEl.querySelector<HTMLInputElement | HTMLTextAreaElement>(
        `input[data-in="${keep}"], textarea[data-in="${keep}"]`);
      if (back) focusAt(back, caret);
    }
  }

  private renderFoot() {
    if (this.footEl.childElementCount > 0) return;
    // The imprint, the privacy notice and the terms are separate
    // destinations and they are one press from every page. A paid service
    // operated from Germany needs that, and a menu is a page.
    // A telephone takes the short name of the privacy notice and of the
    // terms (R732). Both names ride in the markup and the stylesheet
    // swaps them, so a resize needs no second render. A link whose short
    // name differs keeps the whole name for a screen reader at every width
    // (R2185): ABOUT on a telephone is ABOUT THIS PROJECT.
    this.footEl.innerHTML = FOOT_HREFS
      .map((href, i) => {
        const said = S.foot_names_short[i] === S.foot_names[i] ? '' : ` aria-label="${S.foot_names[i]}"`;
        return `<a href="${href}"${said}><span class="wide">${S.foot_names[i]}</span>`
          + `<span class="narrow">${S.foot_names_short[i]}</span></a>`;
      }).join('');
  }

  /** The fade shows only while content is still below (R229). */
  private updateFade = () => {
    const el = this.innerEl;
    const more = el.scrollHeight - el.scrollTop - el.clientHeight > 4;
    this.paneEl.classList.toggle('more', more);
  };

  private onScroll = () => this.updateFade();

  private onResize = () => {
    this.lastPane = '';
    this.render(true);
  };

  // --- presses ---

  /** Runs one control. `PressRoute` calls it on the press in the fixed
   *  chrome and on the release inside the pane (R1206). */
  private onAct = (el: HTMLElement) => {
    const act = el.dataset.act ?? '';
    // A press anywhere else puts the armed SIGN OUT control back at rest,
    // so one armed control can never fire from another row.
    if (act !== 'signout') this.gate.clear(false);
    switch (act) {
      case 'tab':
        this.select((el.dataset.tab ?? 'play') as TabKey,
          el.dataset.section === undefined ? undefined : { section: el.dataset.section });
        return;
      case 'signout':
        // The gate plays no sound of its own. The arming press gets the
        // cue here; `signOut` plays the same cue on the press that fires.
        if (!this.gate.press('signout')) { this.audio?.play('sfx-menu-press'); return; }
        this.signOut();
        return;
      case 'rename':
        this.audio?.play('sfx-menu-press');
        this.rename = renameOpen(this.rename, this.me.account.name);
        this.render(true);
        this.renameBox()?.focus();
        return;
      case 'rename-cancel':
        this.audio?.play('sfx-menu-back');
        this.rename = renameClose(this.rename);
        this.render(true);
        return;
      case 'rename-save':
        this.askRename();
        return;
      case 'rename-back':
        this.audio?.play('sfx-menu-back');
        this.rename = renameBack(this.rename);
        this.render(true);
        this.renameBox()?.focus();
        return;
      case 'rename-confirm':
        this.submitRename();
        return;
      case 'notice-dismiss':
        this.dismiss(Number(el.dataset.id ?? '0'));
        return;
      default:
        this.panels.get(this.tab)?.act?.(act, el);
    }
  };

  /**
   * Removes one notice card of a moderation decision (R3125). The card goes
   * from the page at once, and a refused call brings it back with the
   * refusal line.
   */
  private dismiss(id: number) {
    const held = this.me.notices ?? [];
    if (!held.some((n) => n.id === id)) return;
    this.audio?.play('sfx-menu-back');
    this.me = { ...this.me, notices: held.filter((n) => n.id !== id) };
    this.render(true);
    dismissNotice(id).catch((err: unknown) => {
      this.me = { ...this.me, notices: held };
      this.error = messageOf(err);
      this.render(true);
    });
  }

  /** The rename box is wired here and not in a panel, because it lives in
   *  the header. Every other input belongs to the open section. */
  private wireRenameInput() {
    const box = this.renameBox();
    if (!box || !wireOnce(box)) return;
    box.addEventListener('input', () => { this.rename = renameTyped(this.rename, box.value); });
    box.addEventListener('keydown', (ev) => {
      // Enter asks for the confirmation. It never sends the call: a
      // one-time change must not leave on one key press (R249).
      if (ev.key === 'Enter') this.askRename();
      if (ev.key === 'Escape') {
        this.rename = renameClose(this.rename);
        this.render(true);
      }
    });
  }
}
